'use client';
import { IProduct } from '@mybestbrands/types';

// Utility function to capitalize the first letter of each word
const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const ProductTitle = ({ product }: { product: IProduct }) => {
  const { name, productLines, colors } = product;

  // Create an array of title parts (name and product lines, but NOT colors yet)
  const titleParts = [name, productLines && productLines[0]].filter(Boolean).join(' ').split(/\s+/); // Split by whitespace only

  // Capitalize colors[0] (leave the rest of the title unchanged)
  const color = colors && colors[0] ? capitalizeWords(colors[0]) : null;

  // Function to check if color is part of any longer word (e.g., "Hellbraun" contains "Braun")
  // Only applies to cases where a shorter color might overlap with a longer one
  const colorPartOfLongerWord = (color: string, title: string[]): boolean => {
    const lowerColor = color.toLowerCase();
    return title.some((word) => {
      // Remove punctuation like commas for comparison
      const cleanedWord = word.toLowerCase().replace(/[.,]/g, '');
      return cleanedWord !== lowerColor && cleanedWord.includes(lowerColor) && lowerColor.length < cleanedWord.length;
    });
  };

  // Function to remove only exact occurrences of the color
  const removeExactColorOccurrences = (color: string, title: string[]): string[] => {
    const lowerColor = color.toLowerCase();
    const capitalizedColor = capitalizeWords(lowerColor);
    const colorWithComma = `${lowerColor},`;
    const capitalizedColorWithComma = `${capitalizedColor},`;

    return title.filter((word) => {
      const normalizedWord = word.toLowerCase();
      return (
        normalizedWord !== lowerColor &&
        normalizedWord !== colorWithComma &&
        word !== capitalizedColor &&
        word !== capitalizedColorWithComma
      );
    });
  };

  // Function to keep only one instance of the color, removing duplicates
  const keepOneColorInstance = (color: string, title: string[]): string[] => {
    const lowerColor = color.toLowerCase();
    const capitalizedColor = capitalizeWords(lowerColor);
    const colorWithComma = `${lowerColor},`;
    const capitalizedColorWithComma = `${capitalizedColor},`;

    let foundFirst = false;

    return title
      .map((word) => {
        const normalizedWord = word.toLowerCase();
        const isDuplicate =
          normalizedWord === lowerColor ||
          normalizedWord === colorWithComma ||
          word === capitalizedColor ||
          word === capitalizedColorWithComma;

        if (isDuplicate) {
          if (!foundFirst) {
            foundFirst = true; // Keep the first occurrence
            return word; // Keep it as is
          }
          return null; // Remove subsequent duplicates
        }
        return word;
      })
      .filter((word): word is string => word !== null); // Filter out null values
  };

  // Main logic
  let finalTitleParts = [...titleParts]; // Keep original case for the final output

  if (color) {
    // Step 1: Check if the color is part of a longer word
    const isPartOfLongerWord = colorPartOfLongerWord(color, titleParts);
    if (isPartOfLongerWord) {
      // If the color is part of a longer word, remove only exact occurrences
      finalTitleParts = removeExactColorOccurrences(color, finalTitleParts);
    } else {
      // Handle duplicates when the color is not part of a longer word
      const colorCount = titleParts.filter((word) => {
        const lowerWord = word.toLowerCase();
        return lowerWord === color.toLowerCase() || lowerWord === color.toLowerCase() + ',';
      }).length;

      if (colorCount > 1) {
        // If more than one duplicate, keep only one instance
        finalTitleParts = keepOneColorInstance(color, finalTitleParts);
      } else if (colorCount === 0) {
        // If there are no duplicates, add the color to the end
        finalTitleParts.push(color);
      }
      // If only one instance, leave it as is and do not add to the end
    }
  }

  // Return the final product title as it is (preserving the original case)
  return finalTitleParts.join(' ');
};

export default ProductTitle;
